import { Link } from 'react-router-dom';
import type { Article } from '@/types/content';

interface ArticleCardProps {
  article: Article;
  variant?: 'grid' | 'list';
}

function Meta({ article }: { article: Article }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
      <span className="flex items-center gap-1.5">
        <i className="ri-calendar-line" aria-hidden="true"></i>
        {article.date}
      </span>
      <span className="flex items-center gap-1.5">
        <i className="ri-time-line" aria-hidden="true"></i>
        {article.readTime}
      </span>
      <span className="flex items-center gap-1.5">
        <i className="ri-user-line" aria-hidden="true"></i>
        {article.author}
      </span>
    </div>
  );
}

export default function ArticleCard({ article, variant = 'grid' }: ArticleCardProps) {
  if (variant === 'list') {
    return (
      <article className="group border-b border-background-200 py-6 last:border-b-0">
        <Link to={`/archives/${article.slug}`} className="flex flex-col gap-4 sm:flex-row sm:gap-6">
          <div className="h-40 w-full shrink-0 overflow-hidden rounded-lg sm:h-32 sm:w-52">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 缅北黑料内幕档案`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
          </div>
          <div className="flex flex-1 flex-col justify-between">
            <div>
              <span className="inline-flex items-center rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
                {article.categoryName}
              </span>
              <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                {article.title}
              </h3>
              <p className="mt-2 line-clamp-2 hidden text-sm leading-relaxed text-foreground-600 sm:block">
                {article.excerpt}
              </p>
            </div>
            <div className="mt-4">
              <Meta article={article} />
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/archives/${article.slug}`} className="flex h-full flex-col">
        <div className="h-48 w-full overflow-hidden bg-background-100">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 缅北黑料内幕档案`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-1 flex-col p-5">
          <span className="inline-flex w-fit items-center rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800">
            {article.categoryName}
          </span>
          <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
            {article.title}
          </h3>
          <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
          <div className="mt-5 flex items-center justify-between">
            <span className="text-xs text-foreground-500">{article.date}</span>
            <span className="flex items-center gap-1 text-xs font-medium text-primary-700">
              阅读档案
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}